import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useShipmentSelector } from '../ShipmentSelector.tsx';
import { NoContractSectionHeader } from './SectionHeader.tsx';
import { TableSpotShipment } from './TableSpotShipment.tsx';
import { NoContractForm } from './types';
import { RejectionReasonField } from './RejectionReasonField';
import { useChargeCategorySuggestions } from 'src/use-cases/invoice/useNoContract';

/**
 * Component for display spot shipment table
 *
 * @param {INoContractContentStep2Props} props - The props for the component.
 * @returns {JSX.Element} The rendered component.
 */
export const NoContractContentStep2Accept = () => {
  const { shipment } = useShipmentSelector();
  const { setValue, watch } = useFormContext<NoContractForm>();
  const formValues = watch();
  const { data: suggestionsData, isFetched: isSuggestionsFetched } = useChargeCategorySuggestions(shipment?.shipmentInvoiceId);

  // Initialize invoiceCharges array in the form once suggestions have settled (avoid race condition
  // where the effect fires before the suggestion API returns and sets charges without suggestions)
  useEffect(() => {
    if (shipment?.invoiceCharges && !formValues.invoiceCharges && isSuggestionsFetched) {
      const suggestions = suggestionsData?.data;
      if (suggestions?.length) {
        const suggestionMap = new Map(suggestions.map((s) => [s.chargeId, s]));
        const chargesWithSuggestions = shipment.invoiceCharges.map((charge) => {
          const suggestion = suggestionMap.get(charge.id);
          if (!suggestion) return charge;
          return {
            ...charge,
            chargeCategory: suggestion.suggestedChargeDomain ?? undefined,
            legType: suggestion.suggestedLegType ?? undefined,
          };
        });
        setValue('invoiceCharges', chargesWithSuggestions);
      } else {
        setValue('invoiceCharges', shipment.invoiceCharges);
      }
    }
  }, [shipment?.invoiceCharges, formValues.invoiceCharges, suggestionsData, isSuggestionsFetched, setValue]);

  // Check if any charge has decision set to false (rejected)
  const hasRejectedCharges = useMemo(() => {
    return formValues.invoiceCharges?.some((charge) => charge.decision === false) ?? false;
  }, [formValues.invoiceCharges]);

  // Clear reason field when component mounts (switching from Reject to Accept)
  // or when rejection field is hidden
  useEffect(() => {
    setValue('reason', '');
  }, [setValue]);

  // Clear reason field when hasRejectedCharges becomes false (field is hidden)
  useEffect(() => {
    if (!hasRejectedCharges) {
      setValue('reason', '');
    }
  }, [hasRejectedCharges, setValue]);

  return (
    <div className='p-24'>
      <NoContractSectionHeader title='Categorize spot shipment charges' description='Specify the category and leg type for all charges in this spot shipment' />

      <div className='mt-24'>
        <TableSpotShipment data={shipment?.invoiceCharges || []} isEditable />
      </div>

      {hasRejectedCharges && (
        <div className='mt-24'>
          <div className='mb-12'>
            <h3 className='text-16 font-semibold text-neutral-90'>Rejection</h3>
            <p className='text-14 text-neutral-60'>Specify the reason of the rejection</p>
          </div>
          <RejectionReasonField />
        </div>
      )}
    </div>
  );
};
